import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../contexts/AuthContext";
import { apiClient } from "../lib/api";

/**
 * Hook for managing user account information
 */
export function useUserAccount() {
    const { user, loading: authLoading } = useAuth();

    // Fetch tier status - only when authenticated
    const {
        data: tierData,
        isLoading: tierLoading,
        error: tierError,
    } = useQuery({
        queryKey: ["user-tier-status"],
        queryFn: () => apiClient.getTierStatus(),
        enabled: !!user && !authLoading,
        staleTime: 5 * 60 * 1000, // 5 minutes
        retry: 1,
    });

    // Fetch legacy account data for backward compatibility
    const {
        data: accountData,
        isLoading: accountLoading,
        error: accountError,
    } = useQuery({
        queryKey: ["user-account"],
        queryFn: () => apiClient.getUserAccount(),
        enabled: !!user && !authLoading,
        staleTime: Infinity,
        retry: 1,
    });

    return {
        // Primary tier data from new system
        tier: tierData?.tier || accountData?.tier || "free",
        paymentPlan: tierData?.paymentPlan || "free",
        tierStatus: tierData?.status || "active",
        expiresAt: tierData?.expires_at,

        // Helper functions for tier checking
        isFree: (tierData?.tier || accountData?.tier || "free") === "free",
        isPlus: (tierData?.tier || accountData?.tier || "free") === "plus",
        isPro: (tierData?.tier || accountData?.tier || "free") === "pro",
        isPremium: ["plus", "pro"].includes(tierData?.tier || accountData?.tier || "free"),

        // Loading and error states
        isLoading: authLoading || tierLoading || accountLoading,
        error: tierError || accountError,

        // Raw data for advanced usage
        tierData,
        accountData,
    };
}
